<template>
  <v-layout right>
    <v-container fluid v-if="!small && !icon">
      <v-layout>
        <v-flex sm12 md4>
          <v-btn flat large color="primary" class="my-1" title="upload labels" :disabled="files.length >= maxFiles || disabled" @click="$refs.fileInput.click()">
            <v-icon dark large class="mx-2">cloud_upload</v-icon>
            {{$t('uploadFileWithMax', {maxFiles})}}
            <input 
              ref="fileInput"
              class="upload-file"
              type="file"
              :multiple="maxFiles > 1"
              :accept="acceptTypes.join(',')"
              @click="clearSelectionCache" 
              @change="fileSelected"
            />
          </v-btn>
        </v-flex>
        <v-flex sm12 md8>
          <v-layout align-center justify-end v-for="(item, index) in files" :key="item.file.name + index">
            <v-flex xs12 sm8 md8 class="text-lg-right text-md-right" v-if="isShowPreview">
              <FsFileChip 
                v-if="item.metadata"
                :file="item.metadata"
                :key="'file' + index"
                width="100%"
                aspect-ratio="1.7"
                class="ma-2"
              />
            </v-flex>
            <v-flex xs12 sm8 md8 class="text-lg-right text-md-right" v-else>
              <v-layout row justify-space-between px-2>
                <span class="mr-2 text-xs-right" style="width: order-sm6em;">{{index + 1}}/{{files.length}}</span>
                <span>{{item.metadata.name}}</span>
              </v-layout>
            </v-flex>
            <v-flex xs6 sm1 md1 class="stretchWidth">
              {{item.file.prettySize}}
            </v-flex>
            <v-flex xs6 sm2 md1 class="ml-2">
              <v-progress-circular
                :size="20"
                :width="3"
                :rotate="360"
                :value="item.task.progress"
                color="teal"
                class="ml-1"
                v-if="item.task.snapshot.state === 'running' && item.task.progress !== 100"
              >
              </v-progress-circular>
              <v-tooltip
                top
                v-else-if="item.task.snapshot.state === 'error'" 
              >
                <template v-slot:activator="{on}">
                  <v-icon
                    v-on="on"
                    color="primary" 
                    @click="retryUploadFile(item)"
                  >refresh</v-icon>
                </template>
                <span>retry upload</span>
              </v-tooltip>
              <v-icon color="teal" class="ml-1" v-else>done</v-icon>
            </v-flex>
            <v-flex xs12 sm3 md1 v-if="!disabled">
              <v-btn 
                dark 
                small 
                color="red" 
                icon 
                flat 
                @click.stop="removeFileFromList(item, index)"
                :disabled="isDisableCancelBtn"
              >
                <v-icon dark>cancel</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>
    <v-btn
      color="primary"
      :icon="icon"
      :flat="icon"
      :disabled="files.length >= maxFiles || disabled"
      @click="$refs.fileInput.click()"
      v-else-if="small || icon"
      :loading="smallBtnUploadStatus === 'uploading' || loading"
    >
      <slot>
        <v-icon>{{iconType}}</v-icon>
      </slot>
      <input ref="fileInput" class="upload-file" type="file" :multiple="maxFiles > 1" :accept="acceptTypes.join(',')" v-on:click="clearSelectionCache" v-on:change="fileSelected" />
    </v-btn>
    <CameraUploadWidget :disabled="files.length >= maxFiles || disabled" v-if="isUseCameraUpload" @capturePhoto="capturePhoto" />
  </v-layout>
</template>

<script>
import firebase from 'firebase/compat/app'
import {imageResize} from '@/utils/tools'
import FsFileChip from './FsFileChip'
import CameraUploadWidget from './CameraUploadWidget'
import {debounce} from 'lodash'
const STATUS = {
  READY: 'ready',
  SUCCESS: 'success',
  PROCESSING: 'processing',
  ERROR: 'error'
}

export default {
  name: 'UploadFileWidget',
  components: {
    FsFileChip,
    CameraUploadWidget
  },
  data () {
    return {
      files: [],
      state: STATUS.READY,
      lastEmittedStatus: null
    }
  },
  beforeDestroy () {
    if (this.removeFilesUponUnmounted) {
      return this.removeRemoteFiles()
    }
  },
  computed: {
    smallBtnUploadStatus () {
      let status = ''
      if (this.files.length === 0) {
        status = 'waiting'
      }
      if (this.files.length > 0) {
        if (
          this.files[0].task
          && this.files[0].task.progress
          && this.files[0].task.progress !== 100
        ) status = 'uploading'
        else status = 'done'
      }
      return status
    }
  },
  watch: {
    clearFiles (value) {
      this.$$cleanData()
    },
    removeFiles (value) {
      if (value) {
        return this.removeRemoteFiles()
      }
    },
    state (status) {
      this.$emit('filesStatus', status)
    }
  },
  methods: {
    emitFilesStatusDebounced: debounce(function (status) {
      console.log(status, this.lastEmittedStatus)
      if (status !== this.lastEmittedStatus) {
        this.$emit('filesStatus', status)
        this.lastEmittedStatus = status
      }
    }, 300),
    capturePhoto (file) {
      return this.fileSelected({target: {
        files: [file]
      }})
    },
    removeRemoteFiles () {
      if (!this.files.length) return Promise.resolve('ok')
      console.log('remove file now')
      let promises = this.files.map(file => {
        return this.deleteFileFromStorage(file)
      })
      
      return Promise.all(promises)
        .then(rtn => {
          this.files = []
          this.$emit('input', [])
        })
    },
    clearSelectionCache (e) {
      e.target.value = null
      this.emitFilesUploadStatus()
    },
    fileSelected (e) {
      if (this.maxFiles && (e.target.files.length + this.files.length > this.maxFiles)) {
        return alert(this.$t('uploadFileMaxQty'))
      }

      Object.values(e.target.files).forEach(newFile => {
        if (!this.isCheckDuplicates && this.checkFilenameExists(newFile)) {
          this.$store.dispatch('showToast', {info: `${this.$t('uploadFileExists')}: ${newFile.name}`})
          console.log('file already selected: ', newFile.name)
          return
        }
        if (this.isResizeImage && newFile.type.includes('image')) {
          imageResize(newFile, {width: 1920}, (img) => {
            this.uploadFile(img)
          }) 
        } else {
          this.uploadFile(newFile)
        }
      })      
    },
    uploadFile (newFile) {
      newFile.prettySize = newFile.size > 1048576 ?
        (Math.round(newFile.size / 1048576) + ' ' + 'MB') 
        :
        newFile.size > 1024 ? 
          (Math.round(newFile.size / 1024) + ' ' + 'KB')
          :
          newFile.size + ' ' + 'B'
      const defaultLimit = newFile.type.includes('video/mp4') ? 10485760 : 5242880
      const fileSizeLimit = this.sizeLimit || defaultLimit
      if (newFile.size > fileSizeLimit) {
        this.$store.dispatch('showToast', {
          info: this.$t('uploadFileTooBig', {
            name: newFile.name,
            fileSizeLimit: fileSizeLimit / 1048576
          }),
          level: 'error'
        })
        return
      }
      const isBackend = this.type === 'supports' || this.useBackendUpload
      const uploadAction = isBackend ? 'uploadSupportAttachment' : 'uploadFiles'

      if (isBackend) {
        const newName = this.getFileName(newFile.name)
        const fileItem = {
          file: newFile,
          metadata: { name: newName },
          task: {
            progress: 0,
            snapshot: { state: 'running' },
            cancel: () => {}
          }
        }
        this.files.push(fileItem)
        this.emitFilesUploadStatus()

        return this.$store.dispatch(uploadAction, {newFile, type: this.type, newName})
          .then(rtn => {
            this.$set(fileItem.task, 'progress', 100)
            this.$set(fileItem.task.snapshot, 'state', 'success')
            this.$set(fileItem, 'metadata', rtn.metadata)
            this.$emit('input', this.getFiles())
            this.$emit('getUploadingFileData', fileItem)
            this.emitFilesUploadStatus()
          })
          .catch(error => {
            this.$set(fileItem.task.snapshot, 'state', 'error')
            this.emitFilesUploadStatus()
            this.$store.dispatch('showToast', {info: this.$t('uploadFileFailed')})
            console.error('error uploading file: ', error)
            throw error
          })
      }

      return this.$store.dispatch(uploadAction, {newFile, type: this.type, newName: this.getFileName(newFile.name)})
        .then(rtn => {
          try {
            this.listenToFileUpload(rtn)
            this.files.push(rtn)
            this.emitFilesUploadStatus()
          } catch (error) {
            throw error
          }
        })
        .catch(error => {
          console.error('error uploading file: ', error)
          throw error
        })
    },
    checkFilenameExists (file) {
      const { name, size } = file
      return this.files.findIndex(item => item.file.name === name && item.file.size === size) !== -1
    },
    getFileName (fileName, index = 0) {
      let fileElements = fileName.split('.')
      let fileExt = fileElements.pop()
      let fileToCheck = index ? fileElements.join('.') + index + '.' + fileExt : fileName
      if (this.files.findIndex(item => item.metadata.name === fileToCheck) !== -1) {
        return this.getFileName(fileName, ++index)
      }

      return fileToCheck
    },
    /**
     * @param {{task: firebase.storage.UploadTask}} file
     */
    listenToFileUpload (file) {
      // Listen for state changes, errors, and completion of the upload.
      file.task.on(firebase.storage.TaskEvent.STATE_CHANGED, // or 'state_changed'
        (snapshot) => {
          // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
          var progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          this.$set(file.task, 'progress', progress)
          this.$emit('input', this.getFiles())
        }, (error) => {
          // A full list of error codes is available at
          // https://firebase.google.com/docs/storage/web/handle-errors
          switch (error.code) {
            case 'storage/retry-limit-exceeded':
            case 'storage/unauthorized': // User doesn't have permission to access the object
            case 'storage/canceled': // User canceled the upload 
            case 'storage/unknown': // Unknown error occurred, inspect error.serverResponse
              
              break
            default:
              break
          }
          console.error(`error upload file (${file.file.name}): ${error.message.substring(18)}`)
          this.state = STATUS.ERROR
          this.$emit('error', {code: error.code, file})
          this.$store.dispatch('showToast', {info: this.$t('uploadFileFailed')})
        }, () => {
          // Upload completed successfully, now we can get the download URL
          file.task.snapshot.ref.getDownloadURL().then(downloadURL => {
            file.metadata.downloadURL = downloadURL
            this.$emit('getUploadingFileData', file)
            console.log('File available at', downloadURL)
          })
          this.emitFilesUploadStatus()
        })
    },
    /**
     * @param {{task: firebase.storage.UploadTask}} file
     */
    removeFileFromList (file, index, noPrompt) {
      if (noPrompt || confirm(this.$t('uploadFileRemove'))) {
        file.task && file.task.cancel()
        if (file.task.snapshot.state === 'success') {
          this.deleteFileFromStorage(file)
            .catch(error => {
              if (error.code === 'storage/object-not-found') {
                // this.files.splice(index, 1)
              } else {
                this.$store.dispatch('showToast', {info: this.$t('uploadFileDeleteFailed')})
                console.log('delete file failed: ', error.code)
              }
              return true
            })
            .then(rtn => {
              this.files.splice(index, 1)
              this.$emit('input', this.getFiles())
            })
        } else {
          this.files.splice(index, 1)
          this.$emit('input', this.getFiles())
        }
        this.emitFilesUploadStatus()
      }
    },
    deleteFileFromStorage (file) {
      return this.$store.dispatch('removeFile', file.metadata.fullPath)
        .catch(error => {
          console.error('error removing file: ', error)
        })
    },
    getFiles () {
      return this.files
        .filter(file => {
          return file.task.progress === 100
        })
        .map(file => {
          return file.metadata
        })
    },
    removeAllFiles () {
      return Promise.all(this.files.map(file => this.deleteFileFromStorage(file)))
        .then(arr => {
          this.$emit('input', [])
          this.files = []
        })
    },
    emitFilesUploadStatus () {
      let isFileUploading = false
      let hasError = false
      this.files.forEach(({task, file, metadata}) => {
        const {snapshot} = task
        snapshot.state === 'running' && (isFileUploading = true)
        snapshot.state === 'error' && (hasError = true)
      })
      if (isFileUploading) {
        this.state !== STATUS.PROCESSING && (this.state = STATUS.PROCESSING)
      } else {
        const state = hasError ? STATUS.ERROR : STATUS.SUCCESS
        this.state !== state && (this.state = state)
      }
      this.$emit('filesStatus', this.smallBtnUploadStatus)
    },
    /**
     * @param {{task: firebase.storage.UploadTask, file: File, metadata: object}} file
     */
    retryUploadFile (file) {
      const isBackend = this.type === 'supports' || this.useBackendUpload
      const uploadAction = isBackend ? 'uploadSupportAttachment' : 'uploadFiles'
      if (isBackend) {
        file.task.snapshot.state = 'running'
        file.task.progress = 0
        this.emitFilesUploadStatus()
        return this.$store.dispatch(uploadAction, {newFile: file.file, type: this.type, newName: this.getFileName(file.file.name)})
          .then(rtn => {
            file.task.progress = 100
            file.task.snapshot.state = 'success'
            file.metadata = rtn.metadata
            this.$emit('input', this.getFiles())
            this.emitFilesUploadStatus()
          })
          .catch(error => {
            file.task.snapshot.state = 'error'
            this.emitFilesUploadStatus()
            this.$store.dispatch('showToast', {info: this.$t('uploadFileFailed')})
            console.error('error retrying upload file: ', error)
          })
      }
      return this.$store.dispatch('uploadFiles', {newFile: file.file, type: this.type, newName: this.getFileName(file.file.name)})
        .then(rtn => {
          file.task = rtn.task
          file.metadata = rtn.metadata
          this.listenToFileUpload(file)
          this.emitFilesUploadStatus()
        })
    },
    removeSingleFile (deleteFile) {
      this.$store.dispatch('removeFile', deleteFile.fullPath)
      this.files = this.files.filter(file => file.metadata.name !== deleteFile.name)
      this.$emit('input', this.getFiles())
    },
    
    $$cleanData (arr) {
      this.state = STATUS.READY
      this.files = arr || []
      this.$emit('input', arr || [])
    },
    $$getRawFiles () {
      return this.files.map(file => file.file)
    }
  },
  props: {
    value: [Object, Array],
    type: String,
    removeFilesUponUnmounted: Boolean, // when confirmed, the uploaded file won't get deleted upon closing
    maxFiles: Number,
    disabled: {
      type: Boolean,
      default: false
    },
    isResizeImage: {
      type: Boolean,
      default: true
    },
    clearFiles: [Boolean, Number], // clear files array, but don't remove file on remote server
    removeFiles: [Boolean, Number], // clear files array, and remove file on remote server
    acceptTypes: {
      type: Array,
      default: function () {
        return ['*']
      }
    },
    small: Boolean,
    icon: Boolean,
    loading: Boolean,
    isDisableCancelBtn: {
      type: Boolean,
      default: false
    },
    iconType: {
      type: String,
      default: 'attachment'
    },
    isCheckDuplicates: {
      type: Boolean,
      default: false
    },
    isShowPreview: {
      type: Boolean,
      default: false
    },
    isUseCameraUpload: {
      type: Boolean,
      default: false
    },
    sizeLimit: {
      type: Number,
      default: 0
    },
    useBackendUpload: {
      type: Boolean,
      default: false
    }
  }
}
</script>

<style scoped>
input[type=file].upload-file {
  display: none;
  top: 0;
  right: 0;
  width: 0;
  text-align: right;
  filter: alpha(opacity=0);
  opacity: 0;
  outline: none;
  cursor: inherit;
}
.returnPackageUploader {
  min-width: 800px;
}
.returnPackageUploader .stretchWidth {
  min-width: 60px;
}
</style>



// WEBPACK FOOTER //
// src/components/UploadFileWidget.vue